import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import { ShieldOff, ShieldCheck, UserX, ChevronDown, ChevronUp } from 'lucide-react';
import userService, { type UserData } from '../../services/user';

function UserStatusManagement() {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await userService.getUsers(showInactive);
      if (response.success && response.data) {
        setUsers(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDeactivate = async (userId: number) => {
    try {
      setTogglingId(userId);
      setError(null);
      const response = await userService.setUserActiveStatus(userId, false);
      if (response.success) {
        // Nothing more to do: the server signs them out when this was the last
        // organisation they had, and leaves their sessions alone otherwise.
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: false } : u));
        setConfirmId(null);
      } else {
        setError('Failed to deactivate user.');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to deactivate user.');
    } finally {
      setTogglingId(null);
    }
  };

  const handleReactivate = async (userId: number) => {
    try {
      setTogglingId(userId);
      setError(null);
      const response = await userService.setUserActiveStatus(userId, true);
      if (response.success) {
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: true } : u));
      } else {
        setError('Failed to reactivate user.');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to reactivate user.');
    } finally {
      setTogglingId(null);
    }
  };

  const activeUsers = users.filter(u => u.is_active !== false);
  const inactiveUsers = users.filter(u => u.is_active === false);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8">
          <p className="text-center text-neutral-600">Loading users...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-red-100 flex items-center justify-center">
              <UserX className="h-5 w-5 text-red-600" />
            </div>
            User Account Status
            <span className="text-sm font-normal text-neutral-500 ml-1">
              — deactivate or reactivate users in this organisation
            </span>
          </CardTitle>
          <div className="flex items-center gap-3">
            {!isCollapsed && (
              <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-neutral-600">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={e => setShowInactive(e.target.checked)}
                  className="h-4 w-4 rounded border-neutral-300 text-red-600 focus:ring-red-500"
                />
                Show inactive users
                {showInactive && inactiveUsers.length > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                    {inactiveUsers.length} inactive
                  </span>
                )}
              </label>
            )}
            <button
              onClick={() => setIsCollapsed(prev => !prev)}
              aria-expanded={!isCollapsed}
              aria-label={isCollapsed ? 'Expand user account status' : 'Collapse user account status'}
              className="p-1 rounded-md text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
            >
              {isCollapsed ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </CardHeader>
      {!isCollapsed && (
        <CardContent>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        <p className="mb-4 text-sm text-neutral-600">
          Deactivating someone removes their access to this organisation only. If they also belong to
          another organisation they keep their account and their access there.
        </p>

        <div className="space-y-3">
          {users.length === 0 && (
            <p className="text-center text-neutral-600 py-4">No users found.</p>
          )}

          {/* Active users */}
          {activeUsers.map(user => {
            const isConfirming = confirmId === user.id;
            const isToggling = togglingId === user.id;
            const displayName = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username;

            return (
              <div
                key={user.id}
                className="border border-neutral-200 rounded-lg p-4 flex items-center justify-between gap-4"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-neutral-900">{displayName}</p>
                    {displayName !== user.username && (
                      <p className="text-sm text-neutral-500">{user.username}</p>
                    )}
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 flex items-center gap-1">
                      <ShieldCheck className="h-3 w-3" /> Active
                    </span>
                  </div>
                  <p className="text-sm text-neutral-500 mt-0.5">{user.email}</p>
                </div>

                <div className="shrink-0 flex items-center gap-2">
                  {isConfirming ? (
                    <>
                      <span className="text-sm text-red-700 font-medium">Deactivate this user?</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeactivate(user.id)}
                        disabled={isToggling}
                        className="border-red-300 text-red-700 hover:bg-red-50"
                      >
                        {isToggling ? 'Saving…' : 'Confirm'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConfirmId(null)}
                        disabled={isToggling}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setConfirmId(user.id)}
                      className="flex items-center gap-1 border-red-200 text-red-600 hover:bg-red-50"
                    >
                      <ShieldOff className="h-3.5 w-3.5" />
                      Deactivate
                    </Button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Inactive users (only shown when toggle is on) */}
          {showInactive && inactiveUsers.length > 0 && (
            <>
              <div className="pt-2 pb-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400">Inactive Users</p>
              </div>
              {inactiveUsers.map(user => {
                const isToggling = togglingId === user.id;
                const displayName = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username;

                return (
                  <div
                    key={user.id}
                    className="border border-neutral-200 rounded-lg p-4 flex items-center justify-between gap-4 bg-neutral-50 opacity-75"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-neutral-500">{displayName}</p>
                        {displayName !== user.username && (
                          <p className="text-sm text-neutral-400">{user.username}</p>
                        )}
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-200 text-neutral-500 flex items-center gap-1">
                          <ShieldOff className="h-3 w-3" /> Inactive
                        </span>
                      </div>
                      <p className="text-sm text-neutral-400 mt-0.5">{user.email}</p>
                    </div>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReactivate(user.id)}
                      disabled={isToggling}
                      className="flex items-center gap-1 border-green-300 text-green-700 hover:bg-green-50 shrink-0"
                    >
                      <ShieldCheck className="h-3.5 w-3.5" />
                      {isToggling ? 'Saving…' : 'Reactivate'}
                    </Button>
                  </div>
                );
              })}
            </>
          )}

          {showInactive && inactiveUsers.length === 0 && (
            <p className="text-center text-neutral-500 text-sm py-2">No inactive users.</p>
          )}
        </div>
      </CardContent>
      )}
    </Card>
  );
}

export default UserStatusManagement;
